import { Translations } from "./Translations";
import {
  AbsenceCauseCode,
  AbsencePeriod,
  AnnualLeavePayment,
  AnnualLeavePaymentKind,
  Avatar,
  HolidayCode,
  HolidaySpecification,
  HolidayYear,
  LegalEntityType,
  HolidayPayCalculation,
} from "../model";
import { Arrays, Dates } from "../util";

/** Logic for Holiday years and other holiday functionality. */
export class HolidaysLogic {

  /**
   * Gets the multiplier that is used in Average Daily Salary calculation (keskipäiväpalkka).
   * @param holidays Number of holidays for the year.
   */
  public static getDailySalaryMultiplier(holidays: number): number {
    const multipliers = [0, 0, 1.8, 2.7, 3.6, 4.5, 5.4, 6.3, 7.2, 8.1, 9.0, 9.9, 10.8, 11.8, 12.7, 13.6, 14.5, 15.5, 16.4, 17.4, 18.3, 19.3, 20.3, 21.3, 22.2, 23.2, 24.1, 25.0, 25.9, 26.9, 27.8];
    if (holidays > 30) {
      return multipliers[30] + (holidays - 30) * 0.9;
    }
    return multipliers[holidays];
  }

  /**
   * Gets a new blank holiday year.
   * @param year Holiday accrual year (e.g. 2019) or 0 for current year and -1 for previous year (from -10 to 10).
   * Current year is always from 1.4. until 31.3. e.g. year 2019 is 1.4.2019 until 31.3.2020.
   * The main part of the holidays from holiday accrual year 2019 are kept on summer 2020.
   */
  public static getBlank(year = 0): HolidayYear {
    if (year < 11) {
      const currentYear = Dates.getMonth(Dates.getToday())! < 4 ? (Dates.getYear("today")! - 1) : Dates.getYear("today")!;
      year = currentYear + year;
    }
    const result: HolidayYear = {
      year,
      workerId: null,
      employmentId: "",
      period: {
        start: `${year}-04-01`,
        end: `${year + 1}-03-31`,
        daysCount: 0,
      },
      accrual: {
        months: [],
      },
      leaves: {
        paid: [],
        planned: [],
      },
      spec: {
        code: HolidayCode.Undefined,
      },
    };
    result.period!.daysCount = Dates.getDuration(result.period!.start!, result.period!.end!).asDays();
    return result;
  }

  /** Gets the salary payable type or default type if both are possible. */
  public static getSalaryPayableType(absenseCauseCode: AbsenceCauseCode | null | undefined): "defaultPaid" | "defaultUnpaid" | "paid" | "unpaid" | undefined {
    const defaults = {
      unpaidLeave: "unpaid",
      personalReason: "defaultUnpaid",
      illness: "defaultPaid",
      partTimeSickLeave: "defaultUnpaid",
      parentalLeave: "defaultUnpaid",
      specialMaternityLeave: "defaultUnpaid",
      rehabilitation: "defaultUnpaid",
      childIllness: "defaultPaid",
      partTimeChildCareLeave: "defaultUnpaid",
      training: "defaultPaid",
      jobAlternationLeave: "unpaid",
      studyLeave: "unpaid",
      industrialAction: "unpaid",
      interruptionInWorkProvision: "unpaid",
      leaveOfAbsence: "defaultUnpaid",
      militaryRefresherTraining: "defaultUnpaid",
      militaryService: "unpaid",
      layOff: "unpaid",
      childCareLeave: "unpaid",
      midWeekHoliday: "paid",
      accruedHoliday: "paid",
      occupationalAccident: "paid",
      annualLeave: "paid",
      partTimeAbsenceDueToRehabilitation: "paid",
      other: "defaultUnpaid",
    };
    return defaults[absenseCauseCode as string];
  }

  /** Gets the needed logic for Absences Period Edit dialog */
  public static getAbsencesEditDialogLogic(): {
    /**
     * Returns true if the select component should be shown for absence isPaid.
     */
    canSelectIsPaid: (current: AbsencePeriod) => boolean;
    /** Updates the IsPaid according to absence type. */
    updateIsPaid: (current: AbsencePeriod) => void;
    /** Updates the IsHolidayAccrual */
    updateIsHolidayAccrual: (current: AbsencePeriod) => void;
    /** Updates the workdays count */
    updatePeriodDays: (current: AbsencePeriod) => void;
    /** Sets the days array in period to enable days selection UI. */
    setDaysSelection: (period: AbsencePeriod) => void;
  } {
    const updateIsHolidayAccrual = (current: AbsencePeriod): void => {
      current.isHolidayAccrual = current.isPaid;
    };

    return {
      canSelectIsPaid: (current: AbsencePeriod): boolean => {
        if (!current.causeCode) {
          return false;
        }
        const type = HolidaysLogic.getSalaryPayableType(current.causeCode);
        return type === "defaultPaid" || type === "defaultUnpaid";
      },
      updateIsPaid: (current: AbsencePeriod): void => {
        const type = HolidaysLogic.getSalaryPayableType(current.causeCode);
        switch (type) {
          case "defaultPaid":
          case "paid":
            current.isPaid = true;
            updateIsHolidayAccrual(current);
            break;
          case "defaultUnpaid":
          case "unpaid":
            current.isPaid = false;
            updateIsHolidayAccrual(current);
            break;
          default:
            // Keep existing
            break;
        }
      },
      updateIsHolidayAccrual,
      updatePeriodDays: (current: AbsencePeriod) => {
        const period = current?.period;
        if (period?.start && period?.end && period.start > period.end) {
          period.end = period.start;
        }
        if (period?.start && period?.end) {
          if (period.days) {
            period.days = Dates.getWorkdays(period.start, period.end);
            period.daysCount = period.days.length;
          } else {
            period.daysCount = Dates.getWorkdays(period.start, period.end).length;
          }
        }
      },
      setDaysSelection: (current: AbsencePeriod) => {
        const period = current?.period ?? {};
        period.days = Dates.getWorkdays(period.start ?? "", period.end ?? "");
        period.daysCount = period.days.length;
      },
    };
  }

  /** Gets different total days calculation of planned leaves. */
  public static getPlannedLeavesCount(holidayYear: HolidayYear, type: "all" | "summer" | "winter" | "holidaysSaldoEnd" | "holidaysSaldoStart" = "all"): number {
    // TODO: Split period if it starts summer and ands winter.
    const year = holidayYear.year;
    const list = holidayYear.leaves?.planned ?? [];
    switch (type) {
      case "all":
        return Arrays.sum(list, (x) => x.period?.daysCount ?? 0);
      case "summer":
        return Arrays.sum(list.filter((x) => x.period?.start && (x.period.start < `${year}-10-1`)), (x) => x.period?.daysCount ?? 0);
      case "winter":
        return Arrays.sum(list.filter((x) => x.period?.start && (x.period.start >= `${year}-10-1`)), (x) => x.period?.daysCount ?? 0);
      case "holidaysSaldoStart":
        return Arrays.sum(holidayYear.accrual?.months ?? [], (x) => x.daysAccrued ?? 0) + (holidayYear.accrual?.startSaldo ?? 0);
      case "holidaysSaldoEnd":
        return (HolidaysLogic.getPlannedLeavesCount(holidayYear, "holidaysSaldoStart") ?? 0) - (HolidaysLogic.getPlannedLeavesCount(holidayYear!, "all") ?? 0);
    }
  }

  /** Gets a calculated total or saldo row for paid holiday pays */
  public static getPaidHolidaysCalculation(
    holidayYear: HolidayYear,
    type: "startSaldo" | "total" | "endSaldo" = "total"
  ): AnnualLeavePayment | undefined {
    switch (type) {
      case "total":
        return {
          avatar: HolidaysLogic.getPaidCalculationAvatar("total", undefined),
          holidayBonus: null,
          holidayCompensation: Arrays.sum(holidayYear.leaves?.paid?.filter(x => !x.isDisabled && ( x.kind == AnnualLeavePaymentKind.PaidCalc || x.kind == AnnualLeavePaymentKind.ManualCompensation )) ?? [], (x) => x.holidayCompensation ?? 0),
          holidayDays: Arrays.sum(holidayYear.leaves?.paid?.filter(x =>
            (
              (x.kind == AnnualLeavePaymentKind.PaidCalc && ((x.holidayCompensation ?? 0) > 0 || (x.holidaySalary ?? 0) > 0) && !x.isDisabled) ||
              ((x.kind == AnnualLeavePaymentKind.ManualSalary || x.kind == AnnualLeavePaymentKind.ManualCompensation)  && !x.isDisabled)
            )
          ) ?? [], (x) => x.holidayDays ?? 0),
          holidaySalary: Arrays.sum(holidayYear.leaves?.paid?.filter(x => !x.isDisabled && ( x.kind == AnnualLeavePaymentKind.PaidCalc || x.kind == AnnualLeavePaymentKind.ManualSalary )) ?? [], (x) => x.holidaySalary ?? 0),
        } as any;
      case "startSaldo": {
        return {
          avatar: HolidaysLogic.getPaidCalculationAvatar("startSaldo", undefined),
          holidayBonus: null,
          holidayDays: holidayYear.accrual?.endSaldo ?? 0,
          holidayCompensation: null,
          holidaySalary: null,
        } as any;
      }
      case "endSaldo": {
        const total = HolidaysLogic.getPaidHolidaysCalculation(holidayYear, "total");
        const startSaldo = HolidaysLogic.getPaidHolidaysCalculation(holidayYear, "startSaldo");
        return {
          avatar: HolidaysLogic.getPaidCalculationAvatar("endSaldo", undefined),
          holidayBonus: null,
          holidayCompensation:  null,
          holidayDays: (startSaldo?.holidayDays ?? 0) - (total?.holidayDays ?? 0),
          holidaySalary: null,
        } as any;
      }
    }
  }

  /** Gets a calculated total or saldo row for paid bonus */
  public static getPaidBonusCalculation(
    holidayYear: HolidayYear,
    type: "startSaldo" | "total" | "endSaldo" = "total"
  ): AnnualLeavePayment {
    switch (type) {
      case "total":
        return {
          avatar: HolidaysLogic.getPaidBonusAvatar("total", undefined),
          holidayBonus: Arrays.sum(holidayYear.leaves?.paid?.filter(x => !x.isDisabled && ( x.kind == AnnualLeavePaymentKind.PaidCalc || x.kind == AnnualLeavePaymentKind.ManualBonus )) ?? [], (x) => x.holidayBonus ?? 0),
          holidayCompensation: null,
          holidayDays: Arrays.sum(holidayYear.leaves?.paid?.filter(x =>
            (
              (x.kind == AnnualLeavePaymentKind.PaidCalc && (x.holidayBonus ?? 0) > 0  && !x.isDisabled) ||
              (x.kind == AnnualLeavePaymentKind.ManualBonus  && !x.isDisabled)
            )
          ) ?? [], (x) => x.holidayDays ?? 0),
          holidaySalary: null,

        } as any;
      case "startSaldo": {
        return {
          avatar: HolidaysLogic.getPaidBonusAvatar("startSaldo", undefined),
          holidayBonus: null,
          holidayDays: holidayYear.accrual?.endSaldo ?? 0,
          holidayCompensation: null,
          holidaySalary: null,
        } as any;
      }
      case "endSaldo": {
        const total = HolidaysLogic.getPaidBonusCalculation(holidayYear, "total");
        const startSaldo = HolidaysLogic.getPaidBonusCalculation(holidayYear, "startSaldo");
        return {
          avatar: HolidaysLogic.getPaidBonusAvatar("endSaldo", undefined),
          holidayBonus: null,
          holidayCompensation:  null,
          holidayDays: (startSaldo?.holidayDays ?? 0) - (total?.holidayDays ?? 0),
          holidaySalary: null,
        } as any;
      }
    }
  }

  /** Gets a display avatar for a paid calculation */
  public static getPaidCalculationAvatar(
    type: "row" | "startSaldo" | "total" | "endSaldo" = "row",
    row: AnnualLeavePayment | undefined,
  ): Avatar {
    switch (type) {
      case "row":
        return {
          displayName: `${Translations.get("SALAXY.CORE.HolidaysLogic.paid")} ${Dates.getFormattedDate(row?.date)}`,
          description: `${Translations.get("SALAXY.CORE.HolidaysLogic.salaryPeriod")} ${Dates.getFormattedRange(row?.period?.start ?? "", row?.period?.end ?? "")}`,
          color: "#ffe66d",
          initials:  Translations.getWithDefault("SALAXY.ENUM.CalculationRowType.holidaySalary.iconText", undefined),
          entityType: LegalEntityType.Company,
        };
      case "startSaldo":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.startSaldo.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.startSaldo.description"),
          color: "gray",
          initials: "saldo",
          entityType: LegalEntityType.Company,
        };
      case "total":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.total.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.total.description"),
          color: "#ffe66d",
          initials: "=",
          entityType: LegalEntityType.Company,
        };
      case "endSaldo":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.endSaldo.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.endSaldo.description"),
          color: "gray",
          initials: "saldo",
          entityType: LegalEntityType.Company,
        };
    }
  }

  /** Gets a display avatar for a paid calculation */
  public static getPaidBonusAvatar(
    type: "row" | "startSaldo" | "total" | "endSaldo" = "row",
    row: AnnualLeavePayment | undefined,
  ): Avatar {
    switch (type) {
      case "row":
        return {
          displayName: `${Translations.get("SALAXY.CORE.HolidaysLogic.paid")} ${Dates.getFormattedDate(row?.date)}`,
          description: `${Translations.get("SALAXY.CORE.HolidaysLogic.salaryPeriod")} ${Dates.getFormattedRange(row?.period?.start ?? "", row?.period?.end ?? "")}`,
          color: "#ffe66d",
          initials: Translations.getWithDefault("SALAXY.ENUM.CalculationRowType.holidayBonus.iconText", undefined),
          entityType: LegalEntityType.Company,
        };
      case "startSaldo":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.bonusStartSaldo.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.bonusStartSaldo.description"),
          color: "gray",
          initials: "saldo",
          entityType: LegalEntityType.Company,
        };
      case "total":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.bonusTotal.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.bonusTotal.description"),
          color: "#ffe66d",
          initials: "=",
          entityType: LegalEntityType.Company,
        };
      case "endSaldo":
        return {
          displayName: Translations.get("SALAXY.CORE.HolidaysLogic.bonusEndSaldo.displayName"),
          description: Translations.get("SALAXY.CORE.HolidaysLogic.bonusEndSaldo.description"),
          color: "gray",
          initials: "saldo",
          entityType: LegalEntityType.Company,
        };
    }
  }

  /**
   * Gets the visualisation data for the a holiday year.
   * @param holidayYear The holiday year to visualize
   */
  public static getYearVisualisation(holidayYear: HolidayYear): any {
    // TODO: Add typing for the visualization
    const year = holidayYear.year;
    const todayYear = Dates.getYear("today")!;
    const todayMonthIndex = Dates.getMonth("today")! - 4;
    const currentMonthIx = todayYear < year ? todayMonthIndex : (todayYear > year ? todayMonthIndex + 24 : todayMonthIndex + 12);
    return {
      year,
      employmentId: holidayYear.employmentId,
      currentMonthIx,
      years: [
        { text: year - 1, months: 9, style: "default" },
        { text: year, months: 12, style: "primary" },
        { text: year + 1, months: 4, style: "default" },
      ],
      months: [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4],
      items: [
        { text: `${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.accrualEndSaldo")}: ${holidayYear.accrual?.endSaldo ?? 0} ${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.day")}`, months: 12, start: 0, style: "default", view: "accrual" },
        { text: `${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.plannedSummerLeaves")}: ${HolidaysLogic.getPlannedLeavesCount(holidayYear, "summer")} ${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.day")}`, months: 5, start: 13, style: "default", view: "holidays-summer" },
        { text: `${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.plannedWinterLeaves")}: ${HolidaysLogic.getPlannedLeavesCount(holidayYear, "winter")} ${Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.day")}`, months: 7, start: 18, style: "default", view: "holidays-winter" },
        { text: Translations.get("SALAXY.CORE.HolidaysLogic.holidayYear.paidHolidays"), months: 12, start: 13, style: "default", view: "paidHolidays" },
      ],
    };
  }

  /**
   * Method for checking if holidays are active
   */
  public static isWorkerHolidaysActive(holidays: HolidaySpecification): boolean {
    return holidays && holidays.code !== HolidayCode.Undefined;
  }

  /** Returns holiday pay daily salary */
  public static getHolidayPayDailySalary(holidayYear: HolidayYear): number {
    if (holidayYear?.spec == null || holidayYear?.holidayPay == null) {
      return 0;
    }

    switch (holidayYear.spec.holidayPayCalculation)
    {
        case HolidayPayCalculation.NormalMonthlyPay:
          return holidayYear.holidayPay?.normalMonthlyPayDailySalary ?? 0;
        case HolidayPayCalculation.AverageDailyPay:
            return holidayYear.holidayPay?.averageDailyPayDailySalary ?? 0;
        case HolidayPayCalculation.AverageHourlyPay:
            return holidayYear.holidayPay?.averageHourlyPayDailySalary ?? 0;
        case HolidayPayCalculation.PercentageBasedPay:
            return holidayYear.holidayPay?.percentageBasedPayDailySalary ?? 0;
        case HolidayPayCalculation.OtherPay:
            return holidayYear.holidayPay?.otherPay ?? 0;
        case HolidayPayCalculation.HolidayCompensation:
            return holidayYear.holidayPay?.compensationPayDailySalary ?? 0;
        case HolidayPayCalculation.ScriptPay:
            return holidayYear.holidayPay?.scriptPay ?? 0;
        default:
            return 0;
    }
  }
}